/**
 * AI structuring for team documents — the AI-STRUCTURE step of the team-knowledge
 * pipeline:
 *
 *   AUTO-COLLECT → AI-STRUCTURE → HUMAN-VERIFY → TEAM-CONSUME
 *
 * TODAY (this batch): a human pastes free-form notes about a document (or the
 * doc's own abstract) — the manual stand-in for AUTO-COLLECT — and this helper
 * uses the REAL Bedrock converse op (`api.inference.converse`) to STRUCTURE
 * those notes into a partial {@link TeamDocumentDraft} (title, docType, summary,
 * contextWhy, tags, related services). The result is returned to the register
 * form so a human can VERIFY/edit before saving; nothing is persisted here and
 * no field is trusted blindly.
 *
 * EXTENSION POINT (AUTO-COLLECT — not available yet): when Quip/Wiki/repo
 * crawlers land, an upstream collector will produce the same free-form "notes"
 * (or richer signals — the doc's title, first paragraphs, owner) automatically
 * and feed them into this exact function; the AI-STRUCTURE → HUMAN-VERIFY →
 * TEAM-CONSUME tail is unchanged. This mirrors how SpecStudio (code-Knowledge)
 * is deferred: the consuming shape is stable, only the source is swapped.
 *
 * Strict layering: this is a pure feature-level helper that receives the
 * {@link InferenceApi} (via the facade) from the hook; it never touches fetch.
 */

import type { InferenceApi } from '../../api';
import { DOCUMENT_TYPES } from './documentConfig';
import type { TeamDocumentDraft } from './types';

/**
 * Model used for structuring. Kept local so the documents feature is not
 * coupled to other features.
 *
 * TODO: Confirm the exact inference profile / model id the backend is
 * provisioned for; this Claude Sonnet id is a sensible default and is swapped
 * once Bedrock model access is finalized for the EngineeringVector account.
 */
export const STRUCTURE_MODEL_ID = 'global.anthropic.claude-haiku-4-5-20251001-v1:0';

const STRUCTURE_MAX_TOKENS = 1024;
const STRUCTURE_TEMPERATURE = 0.1;

/** The subset of draft fields the structurer attempts to populate. */
export type StructuredDocumentFields = Partial<
  Pick<
    TeamDocumentDraft,
    | 'title'
    | 'docType'
    | 'summary'
    | 'contextWhy'
    | 'tags'
    | 'relatedServices'
  >
>;

function buildSystemPrompt(): string {
  const types = DOCUMENT_TYPES.map((t) => t.value).join(', ');
  return [
    'You structure free-form notes ABOUT a document into a concise, factual',
    'JSON object of metadata for a team knowledge index. You are describing the',
    'document, not rewriting it.',
    '',
    'Return ONLY a single JSON object (no prose, no markdown fences) with these',
    'optional string / string[] keys:',
    '- title: the document title',
    `- docType: one of [${types}] (best guess; omit if unclear)`,
    '- summary: a 1-3 sentence summary of what the document says',
    '- contextWhy: why the document exists / the decision or context it captures',
    '- tags: string[] of short discovery tags',
    '- relatedServices: string[] of services/systems the document concerns',
    '',
    'Rules: Only use information present in the notes. Do NOT invent facts,',
    'names, links, or systems. Omit any key you cannot fill from the notes.',
    'Keep values concise. Never include productivity, velocity, commit-count,',
    'or ranking judgements about people.',
  ].join('\n');
}

/** Extracts the first balanced JSON object from a model response string. */
function extractJsonObject(text: string): unknown {
  const trimmed = text.trim();
  const fenced = trimmed.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '');
  const start = fenced.indexOf('{');
  const end = fenced.lastIndexOf('}');
  if (start === -1 || end === -1 || end <= start) {
    throw new Error('The model did not return a JSON object.');
  }
  return JSON.parse(fenced.slice(start, end + 1));
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function asStringList(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const out = value
    .filter((v): v is string => typeof v === 'string')
    .map((v) => v.trim())
    .filter(Boolean);
  return out.length ? out : undefined;
}

/** Coerces a raw docType guess to a known value, else leaves undefined. */
function normalizeDocType(value: unknown): string | undefined {
  const s = asString(value);
  if (!s) return undefined;
  const match = DOCUMENT_TYPES.find(
    (opt) => opt.value.toLowerCase() === s.toLowerCase(),
  );
  return match ? match.value : undefined;
}

/**
 * Structures free-form notes into partial document fields via the real Bedrock
 * converse op. Throws a friendly error if inference fails or the output is not
 * parseable — the caller keeps the human's existing draft intact.
 */
export async function structureDocumentFromNotes(
  inference: InferenceApi,
  notes: string,
): Promise<StructuredDocumentFields> {
  const clean = notes.trim();
  if (!clean) return {};

  const response = await inference.converse({
    modelId: STRUCTURE_MODEL_ID,
    systemPrompt: buildSystemPrompt(),
    prompt: `Notes:\n${clean}\n\nJSON:`,
    maxTokens: STRUCTURE_MAX_TOKENS,
    temperature: STRUCTURE_TEMPERATURE,
  });

  const parsed = extractJsonObject(response.outputText) as Record<string, unknown>;

  const fields: StructuredDocumentFields = {};
  const title = asString(parsed.title);
  if (title) fields.title = title;
  const docType = normalizeDocType(parsed.docType);
  if (docType) fields.docType = docType;
  const summary = asString(parsed.summary);
  if (summary) fields.summary = summary;
  const contextWhy = asString(parsed.contextWhy);
  if (contextWhy) fields.contextWhy = contextWhy;
  const tags = asStringList(parsed.tags);
  if (tags) fields.tags = tags;
  const relatedServices = asStringList(parsed.relatedServices);
  if (relatedServices) fields.relatedServices = relatedServices;

  return fields;
}
